import { Routes, Route, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calendar, User, ShieldCheck, ListChecks, FileText } from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";
import StatusBadge from "../../components/StatusBadge";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";

const NAV = [
  { to: "/espace-client", label: "Tableau de bord", end: true },
  { to: "/espace-client/demandes", label: "Mes demandes" },
  { to: "/espace-client/rendez-vous", label: "Mes rendez-vous" },
  { to: "/espace-client/profil", label: "Mon profil" },
  { to: "/espace-client/confidentialite", label: "Confidentialité" },
];

function useMyAppointments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = () => {
    setLoading(true);
    api.get("/appointments/mine").then((r) => setItems(r.data.appointments || [])).finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);
  return { items, loading, refresh };
}

function Overview() {
  const { user } = useAuth();
  const { items } = useMyAppointments();
  const pending = items.filter((a) => !["CONFIRMED", "COMPLETED", "CANCELLED", "CLOSED"].includes(a.status));
  const confirmed = items.filter((a) => a.status === "CONFIRMED");
  return (
    <div>
      <h1 className="font-heading text-3xl text-primary-blue">Bonjour {user?.first_name} 👋</h1>
      <p className="text-slate-600 mt-1">Voici un résumé de vos demandes.</p>
      <div className="mt-8 grid sm:grid-cols-3 gap-5">
        <Stat icon={ListChecks} label="Demandes en cours" value={pending.length} />
        <Stat icon={Calendar} label="Rendez-vous confirmés" value={confirmed.length} />
        <Stat icon={FileText} label="Total dossiers" value={items.length} />
      </div>
      <div className="mt-10">
        <Link to="/prendre-rendez-vous"><Button className="bg-primary-blue text-white">Nouvelle demande de rendez-vous</Button></Link>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white p-6 rounded-2xl border border-slate-200 card-lux">
      <div className="w-10 h-10 rounded-lg bg-sky-blue flex items-center justify-center"><Icon size={20} className="text-primary-blue" /></div>
      <div className="mt-4 text-slate-500 text-sm">{label}</div>
      <div className="text-3xl font-heading text-primary-blue mt-1">{value}</div>
    </div>
  );
}

function AppointmentsList({ statusFilter, title }) {
  const { items, loading, refresh } = useMyAppointments();
  const filtered = statusFilter ? items.filter((a) => statusFilter.includes(a.status)) : items;

  const cancel = async (id) => {
    if (!window.confirm("Confirmer l'annulation de cette demande ?")) return;
    try {
      await api.post(`/appointments/${id}/status`, { status: "CANCELLED", comment: "Annulation client" });
      toast.success("Demande annulée");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <h1 className="font-heading text-3xl text-primary-blue">{title}</h1>
      {loading && <div className="mt-4 text-slate-500">Chargement…</div>}
      {!loading && filtered.length === 0 && (
        <div className="mt-6 bg-white p-8 rounded-2xl border border-slate-200 text-slate-600">Aucune demande pour le moment.</div>
      )}
      <div className="mt-6 space-y-4">
        {filtered.map((a) => (
          <div key={a.id} className="bg-white p-6 rounded-2xl border border-slate-200 card-lux" data-testid={`client-appt-${a.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-sm text-slate-500">Réf. {a.reference}</div>
                <div className="mt-1 font-heading text-xl text-primary-blue">{a.reason} · {a.preferred_date} ({a.time_preference === "morning" ? "matin" : "après-midi"})</div>
              </div>
              <StatusBadge status={a.status} />
            </div>
            <div className="mt-4 text-sm text-slate-600">Centre : {a.city}</div>
            <div className="mt-4 flex gap-2">
              {!["CONFIRMED", "COMPLETED", "CANCELLED", "CLOSED"].includes(a.status) && (
                <Button onClick={() => cancel(a.id)} variant="outline" className="border-red-300 text-red-700" data-testid={`client-appt-cancel-${a.id}`}>
                  Annuler
                </Button>
              )}
            </div>
            {a.status_history?.length > 0 && (
              <details className="mt-4 text-sm">
                <summary className="cursor-pointer text-primary-blue">Historique du dossier</summary>
                <ul className="mt-3 space-y-1 text-slate-600 pl-4 list-disc">
                  {a.status_history.map((h, i) => (
                    <li key={`${h.at || i}-${h.to}`}>{h.at?.slice(0, 10)} — {h.from || "—"} → <strong>{h.to}</strong> {h.comment ? `· ${h.comment}` : ""}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Profile() {
  const { user, refreshMe } = useAuth();
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", postal_code: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (user) setForm({ first_name: user.first_name || "", last_name: user.last_name || "", phone: user.phone || "", postal_code: user.postal_code || "" });
  }, [user]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch("/users/me", form);
      await refreshMe();
      toast.success("Profil mis à jour");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <h1 className="font-heading text-3xl text-primary-blue">Mon profil</h1>
      <form onSubmit={submit} className="mt-6 bg-white p-6 rounded-2xl border border-slate-200 card-lux grid md:grid-cols-2 gap-5">
        <div><Label>Prénom</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1 h-11" data-testid="profile-first" /></div>
        <div><Label>Nom</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1 h-11" data-testid="profile-last" /></div>
        <div><Label>E-mail (non modifiable)</Label><Input value={user?.email || ""} disabled className="mt-1 h-11 bg-slate-50" /></div>
        <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-11" data-testid="profile-phone" /></div>
        <div><Label>Code postal</Label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className="mt-1 h-11" data-testid="profile-postal" /></div>
        <div className="md:col-span-2"><Button disabled={busy} className="bg-primary-blue text-white" data-testid="profile-save">{busy ? "Enregistrement…" : "Enregistrer"}</Button></div>
      </form>
    </div>
  );
}

function Privacy() {
  const { user, refreshMe } = useAuth();
  const [marketing, setMarketing] = useState(!!user?.marketing_consent);
  useEffect(() => { setMarketing(!!user?.marketing_consent); }, [user]);
  const save = async () => {
    try {
      await api.patch("/users/me", { marketing_consent: marketing });
      await refreshMe();
      toast.success("Préférences mises à jour");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const anonymize = async () => {
    if (!window.confirm("Anonymiser votre compte est irréversible. Continuer ?")) return;
    try {
      await api.post("/users/me/anonymize");
      toast.success("Votre compte a été anonymisé.");
      window.location.href = "/";
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div>
      <h1 className="font-heading text-3xl text-primary-blue">Confidentialité & RGPD</h1>
      <div className="mt-6 bg-white p-6 rounded-2xl border border-slate-200 card-lux space-y-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox checked={marketing} onCheckedChange={(v) => setMarketing(!!v)} className="mt-1" data-testid="privacy-marketing" />
          <span className="text-slate-800">Je souhaite recevoir des informations et conseils d'Audition Expert (facultatif).</span>
        </label>
        <Button onClick={save} className="bg-primary-blue text-white" data-testid="privacy-save">Enregistrer mes préférences</Button>

        <div className="pt-6 border-t border-slate-200">
          <div className="font-heading text-xl text-primary-blue">Mes droits</div>
          <p className="text-slate-600 mt-2">Vous pouvez à tout moment demander la suppression ou l'anonymisation de vos données.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link to="/exercer-mes-droits"><Button variant="outline" className="border-primary-blue text-primary-blue"><ShieldCheck size={16} className="mr-2" /> Exercer un droit</Button></Link>
            <Button onClick={anonymize} variant="outline" className="border-red-300 text-red-700" data-testid="privacy-anonymize">Anonymiser mon compte</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ClientDashboard() {
  return (
    <DashboardLayout title="Espace client" subtitle="Client" nav={NAV}>
      <Routes>
        <Route index element={<Overview />} />
        <Route path="demandes" element={<AppointmentsList title="Mes demandes" statusFilter={["NEW_REQUEST", "ASSIGNED_TO_CENTER", "WAITING_CENTER_RESPONSE", "CENTER_AVAILABLE", "ALTERNATIVE_DATE_PROPOSED", "CENTER_UNAVAILABLE", "PENDING_ADMIN_CONFIRMATION"]} />} />
        <Route path="rendez-vous" element={<AppointmentsList title="Mes rendez-vous" statusFilter={["CONFIRMED", "COMPLETED", "NO_SHOW", "CLOSED"]} />} />
        <Route path="profil" element={<Profile />} />
        <Route path="confidentialite" element={<Privacy />} />
      </Routes>
    </DashboardLayout>
  );
}
